import { Tag, Typography } from 'antd';
import { OrderStatusLabel } from 'components/commons/StatusLabel';
import {
  BATCH_STATUS,
  ORDER_TYPE_CONST,
  PAYMENT_STATUS,
} from 'constants/constants';
import { DynamicObjectType } from 'core/types/common';
import { upperFirst } from 'lodash';
import { Link } from 'react-router-dom';
import { formatDateTime, formatMoney } from 'utils/formattedString';
import ButtonPrintLabel from '../components/ButtonPrintLabel';
import CustomerLabel from '../components/CustomerLabel';
import i18n from 'i18n';
import { newCheckRole } from 'utils/tools';

export const getColumns = (status, roles, byPass) => {
  switch (status) {
    case 'AWAITING_CONFIRMATION':
      return [
        {
          name: 'orderNumber',
          title: 'Order ID',
          render: (d: string, record: DynamicObjectType) => (
            <Link to={`/order-management/${record?.id}/show`}>
              <Typography.Paragraph
                ellipsis={{ rows: 1 }}
                style={{ width: 200, color: '#AB2328' }}
              >
                #{d}
              </Typography.Paragraph>
            </Link>
          ),
          isDefault: true,
        },
        {
          name: 'createdAt',
          title: 'Created Time',
          render: (d) => <p style={{ minWidth: 150 }}>{formatDateTime(d)}</p>,
          width: 150,
          isDefault: true,
          sorter: true,
        },
        {
          name: 'statusOms',
          title: i18n.t('orders.status'),
          render: (d, record) => (
            <div style={{ minWidth: 150 }}>
              <OrderStatusLabel record={record} />
            </div>
          ),
          isDefault: true,
        },
        {
          name: 'paymentMethod',
          title: i18n.t('orders.paymentMethod'),
          render: (d, rc: DynamicObjectType) => (
            <div style={{ minWidth: 120 }}>
              {upperFirst(rc?.payments?.[0]?.type)}
            </div>
          ),
          isDefault: true,
          width: 120,
        },
        {
          name: 'paymentStatus',
          title: i18n.t('orders.paymentStatus'),
          render: (dt: string) => (
            <p style={{ minWidth: 120, color: PAYMENT_STATUS[dt]?.color }}>
              {i18n.t(PAYMENT_STATUS[dt]?.text)}
            </p>
          ),
          isDefault: true,
          width: 120,
        },
        {
          name: 'note',
          title: i18n.t('orders.note'),
          render: (d: string) => (
            <p className="min-w-[200px]">{upperFirst(d)}</p>
          ),
          isDefault: true,
        },
        {
          name: 'customer',
          title: i18n.t('orders.customer'),
          render: (d: DynamicObjectType, rc: DynamicObjectType) => {
            const data =
              rc?.isMarketPlace || !rc?.customerId ? rc?.address : rc?.customer;
            return (
              <div className="color-dark-gray fw-400">{`${data?.firstName || ''} ${data?.lastName || ''}`}</div>
            );
          },
          isDefault: true,
          width: 120,
        },
        {
          name: 'total',
          title: i18n.t('orders.total'),
          render: (d) => <div style={{ minWidth: 120 }}>{formatMoney(d)}</div>,
          isDefault: true,
          width: 120,
          sorter: true,
        },

        {
          name: 'itemsCount',
          title: i18n.t('orders.numberOfItems'),
          render: (d: number) => <p className="min-w-[100px]">{d || 0}</p>,
          isDefault: true,
          sorter: true,
        },
        {
          name: 'channelDisplay',
          title: i18n.t('orders.channel'),
          render: (d: string) => <p>{upperFirst(d)}</p>,
          isDefault: true,
        },
        {
          name: 'locationId',
          title: i18n.t('orders.warehouse'),
          isDefault: true,
        },
        {
          name: 'orderType',
          title: i18n.t('orders.type'),
          render: (d: DynamicObjectType, rc: DynamicObjectType) => (
            <p>{i18n.t(ORDER_TYPE_CONST[rc?.orderType]?.text || '')}</p>
          ),
          isDefault: true,
        },
        {
          name: 'isTo',
          title: i18n.t('orders.isTo'),
          render: (d: boolean) =>
            d ? <span className="text-[#EC5500]">TO</span> : <></>,
          isDefault: true,
        },
      ];
    case 'CONFIRMED':
      return [
        {
          name: 'orderNumber',
          title: 'Order ID',
          render: (d: string, record: DynamicObjectType) => (
            <Link to={`/order-management/${record?.id}/show`}>
              <Typography.Paragraph
                ellipsis={{ rows: 1 }}
                style={{ width: 200, color: '#AB2328' }}
              >
                #{d}
              </Typography.Paragraph>
            </Link>
          ),
          isDefault: true,
        },
        {
          name: 'createdAt',
          title: 'Created Time',
          render: (d) => <p style={{ minWidth: 150 }}>{formatDateTime(d)}</p>,
          width: 150,
          isDefault: true,
          sorter: true,
        },
        {
          name: 'statusOms',
          title: i18n.t('orders.status'),
          render: (d, record) => (
            <div style={{ minWidth: 150 }}>
              <OrderStatusLabel record={record} />
            </div>
          ),
          isDefault: true,
        },
        {
          name: 'paymentMethod',
          title: i18n.t('orders.paymentMethod'),
          render: (d, rc: DynamicObjectType) => (
            <div style={{ minWidth: 120 }}>
              {upperFirst(rc?.payments?.[0]?.type)}
            </div>
          ),
          isDefault: true,
          width: 120,
        },
        {
          name: 'paymentStatus',
          title: i18n.t('orders.paymentStatus'),
          render: (dt: string) => (
            <p style={{ minWidth: 120, color: PAYMENT_STATUS[dt]?.color }}>
              {i18n.t(PAYMENT_STATUS[dt]?.text)}
            </p>
          ),
          isDefault: true,
          width: 120,
        },
        {
          name: 'note',
          title: i18n.t('orders.note'),
          render: (d: string) => (
            <p className="min-w-[200px]">{upperFirst(d)}</p>
          ),
          isDefault: true,
        },
        {
          name: 'customer',
          title: i18n.t('orders.customer'),
          render: (d: DynamicObjectType, rc: DynamicObjectType) => {
            const data =
              rc?.isMarketPlace || !rc?.customerId ? rc?.address : rc?.customer;
            return (
              <div className="color-dark-gray fw-400">{`${data?.firstName || ''} ${data?.lastName || ''}`}</div>
            );
          },
          isDefault: true,
          width: 120,
        },
        {
          name: 'total',
          title: i18n.t('orders.total'),
          render: (d) => <div style={{ minWidth: 120 }}>{formatMoney(d)}</div>,
          isDefault: true,
          width: 120,
          sorter: true,
        },

        {
          name: 'itemsCount',
          title: i18n.t('orders.numberOfItems'),
          render: (d: number) => <p className="min-w-[100px]">{d || 0}</p>,
          isDefault: true,
          sorter: true,
        },
        {
          name: 'channelDisplay',
          title: i18n.t('orders.channel'),
          render: (d: string) => <p>{upperFirst(d)}</p>,
          isDefault: true,
        },
        {
          name: 'locationId',
          title: i18n.t('orders.warehouse'),
          isDefault: true,
        },
        {
          name: 'orderType',
          title: i18n.t('orders.type'),
          render: (d: DynamicObjectType, rc: DynamicObjectType) => (
            <p>{i18n.t(ORDER_TYPE_CONST[rc?.orderType]?.text || '')}</p>
          ),
          isDefault: true,
        },
        {
          name: 'isTo',
          title: i18n.t('orders.isTo'),
          render: (d: boolean) =>
            d ? <span className="text-[#EC5500]">TO</span> : <></>,
          isDefault: true,
        },
      ];
    case 'PICKING':
      return [
        {
          name: 'batchId',
          title: 'Batch ID',
          isDefault: true,
          render: (d: string, record: DynamicObjectType) => (
            <Link to={`/order-management/${record?.id}/detail?status=PICKING`}>
              <Typography.Paragraph
                ellipsis={{ rows: 1 }}
                style={{ width: 150, color: '#AB2328' }}
              >
                {formatDateTime(record?.createdAt, 'DDMMYYYY')}_{record?.id}
              </Typography.Paragraph>
            </Link>
          ),
        },
        {
          name: 'createdAt',
          title: 'Created Time',
          render: (d) => (
            <p style={{ minWidth: 150 }}>{d ? formatDateTime(d) : ''}</p>
          ),
          width: 150,
          isDefault: true,
          sorter: true,
        },
        {
          name: 'sentForPickTime',
          title: 'Batch Sending Time',
          render: (d) => (
            <p style={{ minWidth: 150 }}>{d ? formatDateTime(d) : ''}</p>
          ),
          width: 150,
          isDefault: true,
          sorter: true,
        },
        {
          name: 'orderQuantity',
          title: 'Order Quantity',
          render: (d, rc: DynamicObjectType) => (
            <div className="min-w-[120px]">
              {(rc?.orderCount || 0) - (rc?.orderHasDeliveryCodeCount || 0)}/
              {rc?.orderCount || 0}
            </div>
          ),
          isDefault: true,
        },
        {
          name: 'itemsCount',
          title: 'No of items',
          render: (d, rc: DynamicObjectType) => (
            <p className="min-w-[100px]">{rc?.itemsCount || 0}</p>
          ),
          isDefault: true,
          sorter: true,
        },
        {
          name: 'batchStatus',
          title: 'Batch Status',
          render: (d, rc: DynamicObjectType) => (
            <div
              className={`${BATCH_STATUS[rc?.batchStatus]?.color} min-w-[120px]`}
            >
              {i18n.t(BATCH_STATUS[rc?.batchStatus]?.text)}
            </div>
          ),
          isDefault: true,
        },
        {
          name: 'isTo',
          title: i18n.t('orders.isTo'),
          render: (d: boolean) =>
            d ? <span className="text-[#EC5500]">TO</span> : <></>,
          isDefault: true,
        },
      ];
    case 'PACKING':
      return [
        {
          name: 'batchId',
          title: 'Batch ID',
          isDefault: true,
          render: (d: string, record: DynamicObjectType) => (
            <Link to={`/order-management/${record?.id}/detail?status=PACKING`}>
              <Typography.Paragraph
                ellipsis={{ rows: 1 }}
                style={{ width: 150, color: '#AB2328' }}
              >
                {formatDateTime(record?.createdAt, 'DDMMYYYY')}_{record?.id}
              </Typography.Paragraph>
            </Link>
          ),
          width: 200,
        },
        {
          name: 'createdAt',
          title: 'Created Time',
          render: (d) => (
            <p style={{ minWidth: 150 }}>{d ? formatDateTime(d) : ''}</p>
          ),
          width: 300,
          isDefault: true,
          sorter: true,
        },
        {
          name: 'orderQuantity',
          title: 'Order Quantity',
          render: (d, rc: DynamicObjectType) => (
            <>
              {rc?.orderHasDeliveryCodeCount || 0}/{rc?.orderCount || 0}
            </>
          ),
          isDefault: true,
        },
        {
          name: 'itemsCount',
          title: 'No of items',
          render: (d, rc: DynamicObjectType) => (
            <p className="min-w-[100px]">{rc?.itemsCount || 0}</p>
          ),
          isDefault: true,
          sorter: true,
        },
        {
          name: 'isTo',
          title: i18n.t('orders.isTo'),
          render: (d: boolean) =>
            d ? <span className="text-[#EC5500]">TO</span> : <></>,
          isDefault: true,
        },
        {
          name: 'action',
          title: 'Action',
          render: (d, rc: DynamicObjectType) => {
            return (
              newCheckRole(
                '/label/batch/:batchId/bulk-print-marketplace',
                'POST',
                roles,
                byPass,
              ) && (
                <ButtonPrintLabel
                  isMarketPlace={rc?.isMarketPlace}
                  batchId={rc?.id}
                  count={rc?.orderCount}
                />
              )
            );
          },
          isDefault: true,
        },
      ];
    case 'READY_TO_HANDOVER':
      return [
        {
          name: 'orderNumber',
          title: 'Order ID',
          render: (d: string, record: DynamicObjectType) => (
            <Link to={`/order-management/${record?.id}/show`}>
              <Typography.Paragraph
                ellipsis={{ rows: 1 }}
                style={{ width: 200, color: '#AB2328' }}
              >
                #{d}
              </Typography.Paragraph>
            </Link>
          ),
          isDefault: true,
        },
        {
          name: 'createdAt',
          title: 'Created Time',
          render: (d) => (
            <p style={{ minWidth: 150 }}>{d ? formatDateTime(d) : ''}</p>
          ),
          width: 150,
          isDefault: true,
          sorter: true,
        },
        {
          name: 'statusOms',
          title: i18n.t('orders.status'),
          render: (d, record) => <OrderStatusLabel record={record} />,
          isDefault: true,
        },
        {
          name: 'customer',
          title: i18n.t('orders.customer'),
          render: (d: DynamicObjectType, rc: DynamicObjectType) => {
            const data =
              rc?.isMarketPlace || !rc?.customerId ? rc?.address : rc?.customer;
            return (
              <div className="color-dark-gray fw-400">{`${data?.firstName || ''} ${data?.lastName || ''}`}</div>
            );
          },
          isDefault: true,
          width: 120,
        },
        // {
        //   name: 'orderQuantity',
        //   title: 'Order Quantity',
        //   render: (d, rc: DynamicObjectType) => (
        //     <div className="min-w-[150px]">{rc?.orderBatch?.numberOrder || 0}</div>
        //   ),
        //   isDefault: true,
        // },
        {
          name: 'itemsCount',
          title: i18n.t('orders.numberOfItems'),
          render: (d: number) => <p className="min-w-[100px]">{d || 0}</p>,
          isDefault: true,
          sorter: true,
        },
        {
          name: 'total',
          title: i18n.t('orders.total'),
          render: (d) => <div style={{ minWidth: 120 }}>{formatMoney(d)}</div>,
          isDefault: true,
          width: 120,
          sorter: true,
        },
        {
          name: 'saleOrderId',
          title: i18n.t('orders.saleOrderId'),
          render: (d: string) => <div className="min-w-[120px]">{d}</div>,
          isDefault: true,
        },
        {
          name: 'paymentMethod',
          title: i18n.t('orders.paymentMethod'),
          render: (d, rc: DynamicObjectType) => (
            <div style={{ minWidth: 120 }}>{rc?.payments?.[0]?.type}</div>
          ),
          isDefault: true,
          width: 120,
        },
        {
          name: 'paymentStatus',
          title: i18n.t('orders.paymentStatus'),
          render: (dt: string) => (
            <p style={{ minWidth: 120, color: PAYMENT_STATUS[dt]?.color }}>
              {i18n.t(PAYMENT_STATUS[dt]?.text)}
            </p>
          ),
          isDefault: true,
          width: 120,
        },
        {
          name: 'channelDisplay',
          title: i18n.t('orders.channel'),
          render: (d: string) => <p>{upperFirst(d)}</p>,
        },
        {
          name: 'partner',
          title: i18n.t('orders.deliveryPartner'),
          render: (dt: DynamicObjectType, rc: DynamicObjectType) => {
            return (
              <div className="min-w-[120px]">
                {rc?.shippingCarrier?.name || ''}
              </div>
            );
          },
        },
        {
          name: 'deliveryCode',
          title: i18n.t('orders.shippingNo'),
          render: (dt, rc) => {
            return <div className="min-w-[150px]">{rc?.deliveryCode}</div>;
          },
        },
        {
          name: 'wspId',
          title: i18n.t('orders.wspId'),
          render: (d: string) => <p style={{ minWidth: 100 }}>{d || ''}</p>,
        },
        {
          name: 'orderType',
          title: i18n.t('orders.type'),
          render: (d: DynamicObjectType, rc: DynamicObjectType) => (
            <p>{i18n.t(ORDER_TYPE_CONST[rc?.orderType]?.text || '')}</p>
          ),
        },
        {
          name: 'locationId',
          title: i18n.t('orders.warehouse'),
        },
        {
          name: 'note',
          title: i18n.t('orders.note'),
          render: (d: string) => (
            <p className="min-w-[200px]">{upperFirst(d)}</p>
          ),
        },
        {
          name: 'isTo',
          title: i18n.t('orders.isTo'),
          render: (d: boolean) =>
            d ? <span className="text-[#EC5500]">TO</span> : <></>,
          isDefault: true,
        },
      ];
    case 'READY_TO_PICK':
      return [
        {
          name: 'orderNumber',
          title: 'Order ID',
          render: (d: string, record: DynamicObjectType) => (
            <Link to={`/order-management/${record?.id}/show`}>
              <Typography.Paragraph
                ellipsis={{ rows: 1 }}
                style={{ width: 200, color: '#AB2328' }}
              >
                #{d}
              </Typography.Paragraph>
            </Link>
          ),
          isDefault: true,
        },
        {
          name: 'createdAt',
          title: 'Created Time',
          render: (d) => (
            <p style={{ minWidth: 150 }}>{d ? formatDateTime(d) : ''}</p>
          ),
          width: 150,
          isDefault: true,
          sorter: true,
        },
        {
          name: 'statusOms',
          title: i18n.t('orders.status'),
          render: (d, record) => (
            <div style={{ minWidth: 120 }}>
              <OrderStatusLabel record={record} />
            </div>
          ),
          isDefault: true,
        },
        {
          name: 'saleOrderId',
          title: i18n.t('orders.saleOrderId'),
          render: (d: string) => <div className="min-w-[120px]">{d}</div>,
          isDefault: true,
        },
        {
          name: 'customer',
          title: i18n.t('orders.customer'),
          render: (d: DynamicObjectType, rc: DynamicObjectType) => {
            const data =
              rc?.isMarketPlace || !rc?.customerId ? rc?.address : rc?.customer;
            return (
              <div className="color-dark-gray fw-400">{`${data?.firstName || ''} ${data?.lastName || ''}`}</div>
            );
          },
          isDefault: true,
          width: 120,
        },
        {
          name: 'itemsCount',
          title: i18n.t('orders.numberOfItems'),
          render: (d: number) => <p className="min-w-[100px]">{d || 0}</p>,
          isDefault: true,
          sorter: true,
        },

        {
          name: 'channelDisplay',
          title: i18n.t('orders.channel'),
          render: (d: string) => <p>{upperFirst(d)}</p>,
        },
        {
          name: 'paymentMethod',
          title: i18n.t('orders.paymentMethod'),
          render: (d, rc: DynamicObjectType) => (
            <div style={{ minWidth: 120 }}>
              {upperFirst(rc?.payments?.[0]?.type)}
            </div>
          ),
          isDefault: true,
          width: 120,
        },
        {
          name: 'paymentStatus',
          title: i18n.t('orders.paymentStatus'),
          render: (dt: string) => (
            <p style={{ minWidth: 120, color: PAYMENT_STATUS[dt]?.color }}>
              {i18n.t(PAYMENT_STATUS[dt]?.text)}
            </p>
          ),
          isDefault: true,
          width: 120,
        },
        {
          name: 'locationId',
          title: i18n.t('orders.warehouse'),
          isDefault: true,
        },
        {
          name: 'orderType',
          title: i18n.t('orders.type'),
          render: (d: DynamicObjectType, rc: DynamicObjectType) => (
            <p>{i18n.t(ORDER_TYPE_CONST[rc?.orderType]?.text || '')}</p>
          ),
        },
        {
          name: 'isTo',
          title: i18n.t('orders.isTo'),
          render: (d: boolean) =>
            d ? <span className="text-[#EC5500]">TO</span> : <></>,
          isDefault: true,
        },
      ];
    case 'DELIVERING':
      return [
        {
          name: 'orderNumber',
          title: 'Order ID',
          render: (d: string, record: DynamicObjectType) => (
            <Link to={`/order-management/${record?.id}/show`}>
              <Typography.Paragraph
                ellipsis={{ rows: 1 }}
                style={{ width: 200, color: '#AB2328' }}
              >
                #{d}
              </Typography.Paragraph>
            </Link>
          ),
          isDefault: true,
        },
        {
          name: 'createdAt',
          title: 'Created Time',
          render: (d) => (
            <p style={{ minWidth: 150 }}>{d ? formatDateTime(d) : ''}</p>
          ),
          width: 150,
          isDefault: true,
          sorter: true,
        },
        {
          name: 'statusOms',
          title: i18n.t('orders.status'),
          render: (d, record) => <OrderStatusLabel record={record} />,
          isDefault: true,
        },
        {
          name: 'handedOverTime',
          title: i18n.t('orders.handedOverTime'),
          render: (d) => (
            <div style={{ minWidth: 120 }}>{d ? formatDateTime(d) : ''}</div>
          ),
        },
        {
          name: 'saleOrderId',
          title: i18n.t('orders.saleOrderId'),
          isDefault: true,
          render: (d: string) => <div className="min-w-[120px]">{d}</div>,
        },
        {
          name: 'partner',
          title: i18n.t('orders.deliveryPartner'),
          render: (dt: DynamicObjectType, rc: DynamicObjectType) => {
            return (
              <div className="min-w-[120px]">
                {rc?.shippingCarrier?.name || ''}
              </div>
            );
          },
          isDefault: true,
        },
        // {
        //   name: 'shippingStatus',
        //   title: i18n.t('orders.shippingStatus'),
        //   isDefault: true,
        // },
        {
          name: 'deliveryCode',
          title: i18n.t('orders.shippingNo'),
          render: (dt, rc) => {
            return <div className="min-w-[150px]">{rc?.deliveryCode}</div>;
          },
          isDefault: true,
        },
        {
          name: 'paymentMethod',
          title: i18n.t('orders.paymentMethod'),
          render: (d, rc: DynamicObjectType) => (
            <div style={{ minWidth: 120 }}>
              {upperFirst(rc?.payments?.[0]?.type)}
            </div>
          ),
          isDefault: true,
          width: 120,
        },
        {
          name: 'paymentStatus',
          title: i18n.t('orders.paymentStatus'),
          render: (dt: string) => (
            <p style={{ minWidth: 120, color: PAYMENT_STATUS[dt]?.color }}>
              {i18n.t(PAYMENT_STATUS[dt]?.text)}
            </p>
          ),
          isDefault: true,
          width: 120,
        },
        {
          name: 'customer',
          title: i18n.t('orders.customer'),
          render: (d: DynamicObjectType, rc: DynamicObjectType) => {
            return (
              <CustomerLabel
                data={
                  rc?.isMarketPlace || !rc?.customerId
                    ? rc?.address
                    : rc?.customer
                }
              />
            );
          },
          width: 120,
        },
        {
          name: 'note',
          title: i18n.t('orders.note'),
          render: (d: string) => (
            <p className="min-w-[200px]">{upperFirst(d)}</p>
          ),
        },
        {
          name: 'itemsCount',
          title: i18n.t('orders.numberOfItems'),
          render: (d: number) => <p className="min-w-[100px]">{d || 0}</p>,
          sorter: true,
        },
        {
          name: 'total',
          title: i18n.t('orders.total'),
          render: (d) => <div style={{ minWidth: 120 }}>{formatMoney(d)}</div>,
          width: 120,
          sorter: true,
        },
        {
          name: 'channelDisplay',
          title: i18n.t('orders.channel'),
          render: (d: string) => <p>{upperFirst(d)}</p>,
        },
        {
          name: 'locationId',
          title: i18n.t('orders.warehouse'),
        },
        {
          name: 'orderType',
          title: i18n.t('orders.type'),
          render: (d: DynamicObjectType, rc: DynamicObjectType) => (
            <p>{i18n.t(ORDER_TYPE_CONST[rc?.orderType]?.text || '')}</p>
          ),
        },
        {
          name: 'isTo',
          title: i18n.t('orders.isTo'),
          render: (d: boolean) =>
            d ? <span className="text-[#EC5500]">TO</span> : <></>,
          isDefault: true,
        },
      ];
    case 'FAILED_DELIVERY':
      return [
        {
          name: 'orderNumber',
          title: 'Order ID',
          render: (d: string, record: DynamicObjectType) => (
            <Link to={`/order-management/${record?.id}/show`}>
              <Typography.Paragraph
                ellipsis={{ rows: 1 }}
                style={{ width: 200, color: '#AB2328' }}
              >
                #{d}
              </Typography.Paragraph>
            </Link>
          ),
          isDefault: true,
        },
        {
          name: 'createdAt',
          title: 'Created Time',
          render: (d) => (
            <p style={{ minWidth: 150 }}>{d ? formatDateTime(d) : ''}</p>
          ),
          width: 150,
          isDefault: true,
          sorter: true,
        },
        {
          name: 'statusOms',
          title: i18n.t('orders.status'),
          render: (d, record) => <OrderStatusLabel record={record} />,
          isDefault: true,
        },
        {
          name: 'deliveryFailedTime',
          title: i18n.t('orders.deliveryFailedTime'),
          render: (d) => (
            <div style={{ minWidth: 200 }}>{d ? formatDateTime(d) : ''}</div>
          ),
          isDefault: true,
        },
        {
          name: 'saleOrderId',
          title: i18n.t('orders.saleOrderId'),
          isDefault: true,
          render: (d: string) => <div className="min-w-[120px]">{d}</div>,
        },
        {
          name: 'partner',
          title: i18n.t('orders.deliveryPartner'),
          render: (dt: DynamicObjectType, rc: DynamicObjectType) => {
            return (
              <div className="min-w-[120px]">
                {rc?.shippingCarrier?.name || ''}
              </div>
            );
          },
          isDefault: true,
        },
        {
          name: 'deliveryCode',
          title: i18n.t('orders.shippingNo'),
          render: (dt, rc) => {
            return <div className="min-w-[150px]">{rc?.deliveryCode}</div>;
          },
          isDefault: true,
        },
        {
          name: 'customer',
          title: i18n.t('orders.customer'),
          render: (d: DynamicObjectType, rc: DynamicObjectType) => {
            const data =
              rc?.isMarketPlace || !rc?.customerId ? rc?.address : rc?.customer;
            return (
              <div className="color-dark-gray fw-400">{`${data?.firstName || ''} ${data?.lastName || ''}`}</div>
            );
          },
          isDefault: true,
          width: 120,
        },
        {
          name: 'itemsCount',
          title: i18n.t('orders.numberOfItems'),
          render: (d: number) => <p className="min-w-[100px]">{d || 0}</p>,
          isDefault: true,
          sorter: true,
        },
        {
          name: 'total',
          title: i18n.t('orders.total'),
          render: (d) => <div style={{ minWidth: 120 }}>{formatMoney(d)}</div>,
          isDefault: true,
          width: 120,
          sorter: true,
        },
        {
          name: 'paymentMethod',
          title: i18n.t('orders.paymentMethod'),
          render: (d, rc: DynamicObjectType) => (
            <div style={{ minWidth: 120 }}>
              {upperFirst(rc?.payments?.[0]?.type)}
            </div>
          ),
          isDefault: true,
          width: 120,
        },
        {
          name: 'paymentStatus',
          title: i18n.t('orders.paymentStatus'),
          render: (dt: string) => (
            <p style={{ minWidth: 120, color: PAYMENT_STATUS[dt]?.color }}>
              {i18n.t(PAYMENT_STATUS[dt]?.text)}
            </p>
          ),
          isDefault: true,
          width: 120,
        },
        {
          name: 'note',
          title: i18n.t('orders.note'),
          render: (d: string) => (
            <p className="min-w-[200px]">{upperFirst(d)}</p>
          ),
        },
        {
          name: 'channelDisplay',
          title: i18n.t('orders.channel'),
          render: (d: string) => <p>{upperFirst(d)}</p>,
        },
        {
          name: 'locationId',
          title: i18n.t('orders.warehouse'),
        },
        {
          name: 'orderType',
          title: i18n.t('orders.type'),
          render: (d: DynamicObjectType, rc: DynamicObjectType) => (
            <p>{i18n.t(ORDER_TYPE_CONST[rc?.orderType]?.text || '')}</p>
          ),
        },
        {
          name: 'isTo',
          title: i18n.t('orders.isTo'),
          render: (d: boolean) =>
            d ? <span className="text-[#EC5500]">TO</span> : <></>,
          isDefault: true,
        },
      ];
    case 'RETURNING':
      return [
        {
          name: 'orderNumber',
          title: 'Order ID',
          render: (d: string, record: DynamicObjectType) => (
            <Link to={`/order-management/${record?.id}/show`}>
              <Typography.Paragraph
                ellipsis={{ rows: 1 }}
                style={{ width: 200, color: '#AB2328' }}
              >
                {d}
              </Typography.Paragraph>
            </Link>
          ),
          isDefault: true,
        },
        {
          name: 'createdAt',
          title: 'Created Time',
          render: (d) => <p style={{ minWidth: 150 }}>{formatDateTime(d)}</p>,
          width: 150,
          isDefault: true,
          sorter: true,
        },
        {
          name: 'statusOms',
          title: i18n.t('orders.status'),
          render: (d, record) => (
            <div style={{ minWidth: 150 }}>
              <OrderStatusLabel record={record} />
            </div>
          ),
          isDefault: true,
        },
        {
          name: 'returnedTime',
          title: i18n.t('orders.returnedTime'),
          render: (d) => (
            <div style={{ minWidth: 120 }}>{d ? formatDateTime(d) : ''}</div>
          ),
          isDefault: true,
        },
        {
          name: 'saleOrderId',
          title: i18n.t('orders.saleOrderId'),
          isDefault: true,
          render: (d: string) => <p style={{ minWidth: 150 }}>{d || ''}</p>,
        },
        {
          name: 'partner',
          title: i18n.t('orders.deliveryPartner'),
          render: (dt: DynamicObjectType, rc: DynamicObjectType) => {
            return (
              <div className="min-w-[120px]">
                {rc?.shippingCarrier?.name || ''}
              </div>
            );
          },
        },
        {
          name: 'shippingStatus',
          title: i18n.t('orders.shippingStatus'),
          isDefault: true,
        },
        {
          name: 'deliveryCode',
          title: i18n.t('orders.shippingNo'),
          render: (dt, rc) => {
            return <div className="min-w-[150px]">{rc?.deliveryCode}</div>;
          },
        },
        {
          name: 'paymentMethod',
          title: i18n.t('orders.paymentMethod'),
          render: (d, rc: DynamicObjectType) => (
            <div style={{ minWidth: 120 }}>
              {upperFirst(rc?.payments?.[0]?.type)}
            </div>
          ),
          isDefault: true,
          width: 120,
        },
        {
          name: 'paymentStatus',
          title: i18n.t('orders.paymentStatus'),
          render: (dt: string) => (
            <p style={{ minWidth: 120, color: PAYMENT_STATUS[dt]?.color }}>
              {i18n.t(PAYMENT_STATUS[dt]?.text)}
            </p>
          ),
          isDefault: true,
          width: 120,
        },
        {
          name: 'customer',
          title: i18n.t('orders.customer'),
          render: (d: DynamicObjectType, rc: DynamicObjectType) => {
            const data =
              rc?.isMarketPlace || !rc?.customerId ? rc?.address : rc?.customer;
            return (
              <div className="color-dark-gray fw-400">{`${data?.firstName || ''} ${data?.lastName || ''}`}</div>
            );
          },
          isDefault: true,
          width: 120,
        },
        {
          name: 'itemsCount',
          title: i18n.t('orders.numberOfItems'),
          render: (d: number) => <p className="min-w-[100px]">{d || 0}</p>,
          isDefault: true,
          sorter: true,
        },
        {
          name: 'total',
          title: i18n.t('orders.total'),
          render: (d) => <div style={{ minWidth: 120 }}>{formatMoney(d)}</div>,
          isDefault: true,
          width: 120,
          sorter: true,
        },
        {
          name: 'channelDisplay',
          title: i18n.t('orders.channel'),
          render: (d: string) => <p>{upperFirst(d)}</p>,
        },
        {
          name: 'locationId',
          title: i18n.t('orders.warehouse'),
        },
        {
          name: 'note',
          title: i18n.t('orders.note'),
          render: (d: string) => (
            <p className="min-w-[200px]">{upperFirst(d)}</p>
          ),
        },
        {
          name: 'orderType',
          title: i18n.t('orders.type'),
          render: (d: DynamicObjectType, rc: DynamicObjectType) => (
            <p>{i18n.t(ORDER_TYPE_CONST[rc?.orderType]?.text || '')}</p>
          ),
        },
        {
          name: 'isTo',
          title: i18n.t('orders.isTo'),
          render: (d: boolean) =>
            d ? <span className="text-[#EC5500]">TO</span> : <></>,
          isDefault: true,
        },
      ];
    case 'LOST':
      return [
        {
          name: 'orderNumber',
          title: 'Order ID',
          render: (d: string, record: DynamicObjectType) => (
            <Link to={`/order-management/${record?.id}/show`}>
              <Typography.Paragraph
                ellipsis={{ rows: 1 }}
                style={{ width: 200, color: '#AB2328' }}
              >
                #{d}
              </Typography.Paragraph>
            </Link>
          ),
          isDefault: true,
        },
        {
          name: 'createdAt',
          title: 'Created Time',
          render: (d) => <p style={{ minWidth: 150 }}>{formatDateTime(d)}</p>,
          width: 150,
          isDefault: true,
          sorter: true,
        },
        {
          name: 'statusOms',
          title: i18n.t('orders.status'),
          render: (d, record) => (
            <div style={{ minWidth: 150 }}>
              <OrderStatusLabel record={record} />
            </div>
          ),
          isDefault: true,
        },
        {
          name: 'lostTime',
          title: i18n.t('orders.lostTime'),
          render: (d) => <p style={{ minWidth: 150 }}>{formatDateTime(d)}</p>,
          isDefault: true,
        },
        {
          name: 'lostReason',
          title: i18n.t('orders.lostReason'),
          isDefault: true,
        },
        {
          name: 'saleOrderId',
          isDefault: true,
          title: i18n.t('orders.saleOrderId'),
          render: (d: string) => <div className="min-w-[120px]">{d}</div>,
        },
        {
          name: 'partner',
          title: i18n.t('orders.deliveryPartner'),
          render: (dt: DynamicObjectType, rc: DynamicObjectType) => {
            return (
              <div className="min-w-[120px]">
                {rc?.shippingCarrier?.name || ''}
              </div>
            );
          },
          isDefault: true,
        },
        {
          name: 'deliveryCode',
          title: i18n.t('orders.shippingNo'),
          render: (dt, rc) => {
            return <div className="min-w-[150px]">{rc?.deliveryCode}</div>;
          },
        },
        {
          name: 'customer',
          title: i18n.t('orders.customer'),
          render: (d: DynamicObjectType, rc: DynamicObjectType) => {
            const data =
              rc?.isMarketPlace || !rc?.customerId ? rc?.address : rc?.customer;
            return (
              <div className="color-dark-gray fw-400">{`${data?.firstName || ''} ${data?.lastName || ''}`}</div>
            );
          },
          isDefault: true,
          width: 120,
        },
        {
          name: 'paymentMethod',
          title: i18n.t('orders.paymentMethod'),
          render: (d, rc: DynamicObjectType) => (
            <div style={{ minWidth: 120 }}>
              {upperFirst(rc?.payments?.[0]?.type)}
            </div>
          ),
          isDefault: true,
          width: 120,
        },
        {
          name: 'paymentStatus',
          title: i18n.t('orders.paymentStatus'),
          render: (dt: string) => (
            <p style={{ minWidth: 120, color: PAYMENT_STATUS[dt]?.color }}>
              {i18n.t(PAYMENT_STATUS[dt]?.text)}
            </p>
          ),
          isDefault: true,
          width: 120,
        },
        {
          name: 'itemsCount',
          title: i18n.t('orders.numberOfItems'),
          render: (d: number) => <p className="min-w-[100px]">{d || 0}</p>,
          isDefault: true,
          sorter: true,
        },
        {
          name: 'total',
          title: i18n.t('orders.total'),
          render: (d) => <div style={{ minWidth: 120 }}>{formatMoney(d)}</div>,
          isDefault: true,
          width: 120,
          sorter: true,
        },
        {
          name: 'channelDisplay',
          title: i18n.t('orders.channel'),
          render: (d: string) => <p>{upperFirst(d)}</p>,
        },
        {
          name: 'locationId',
          title: i18n.t('orders.warehouse'),
        },
        {
          name: 'note',
          title: i18n.t('orders.note'),
          render: (d: string) => (
            <p className="min-w-[200px]">{upperFirst(d)}</p>
          ),
        },
        {
          name: 'orderType',
          title: i18n.t('orders.type'),
          render: (d: DynamicObjectType, rc: DynamicObjectType) => (
            <p>{i18n.t(ORDER_TYPE_CONST[rc?.orderType]?.text || '')}</p>
          ),
        },
        {
          name: 'isTo',
          title: i18n.t('orders.isTo'),
          render: (d: boolean) =>
            d ? <span className="text-[#EC5500]">TO</span> : <></>,
          isDefault: true,
        },
      ];
    case 'REJECTED':
      return [
        {
          name: 'orderNumber',
          title: 'Order ID',
          render: (d: string, record: DynamicObjectType) => (
            <Link to={`/order-management/${record?.id}/show`}>
              <Typography.Paragraph
                ellipsis={{ rows: 1 }}
                style={{ width: 200, color: '#AB2328' }}
              >
                #{d}
              </Typography.Paragraph>
            </Link>
          ),
          isDefault: true,
        },
        {
          name: 'createdAt',
          title: 'Created Time',
          render: (d) => <p style={{ minWidth: 150 }}>{formatDateTime(d)}</p>,
          width: 150,
          isDefault: true,
          sorter: true,
        },
        {
          name: 'statusOms',
          title: i18n.t('orders.status'),
          render: (d, record) => (
            <div style={{ minWidth: 150 }}>
              <OrderStatusLabel record={record} />
            </div>
          ),
          isDefault: true,
        },
        {
          name: 'returnedTime',
          title: i18n.t('orders.returnedTime'),
          isDefault: true,
        },
        {
          name: 'saleOrderId',
          title: i18n.t('orders.saleOrderId'),
          isDefault: true,
          render: (d: string) => <div className="min-w-[120px]">{d}</div>,
        },
        {
          name: 'partner',
          title: i18n.t('orders.deliveryPartner'),
          render: (dt: DynamicObjectType, rc: DynamicObjectType) => {
            return (
              <div className="min-w-[120px]">
                {rc?.shippingCarrier?.name || ''}
              </div>
            );
          },
        },
        {
          name: 'shippingStatus',
          title: i18n.t('orders.shippingStatus'),
          isDefault: true,
        },
        {
          name: 'deliveryCode',
          title: i18n.t('orders.shippingNo'),
          render: (dt, rc) => {
            return <div className="min-w-[150px]">{rc?.deliveryCode}</div>;
          },
          isDefault: true,
        },
        {
          name: 'customer',
          title: i18n.t('orders.customer'),
          render: (d: DynamicObjectType, rc: DynamicObjectType) => {
            const data =
              rc?.isMarketPlace || !rc?.customerId ? rc?.address : rc?.customer;
            return (
              <div className="color-dark-gray fw-400">{`${data?.firstName || ''} ${data?.lastName || ''}`}</div>
            );
          },
          isDefault: true,
          width: 120,
        },
        {
          name: 'itemsCount',
          title: i18n.t('orders.numberOfItems'),
          render: (d: number) => <p className="min-w-[100px]">{d || 0}</p>,
          isDefault: true,
          sorter: true,
        },
        {
          name: 'total',
          title: i18n.t('orders.total'),
          render: (d) => <div style={{ minWidth: 120 }}>{formatMoney(d)}</div>,
          isDefault: true,
          width: 120,
          sorter: true,
        },
        {
          name: 'paymentMethod',
          title: i18n.t('orders.paymentMethod'),
          render: (d, rc: DynamicObjectType) => (
            <div style={{ minWidth: 120 }}>
              {upperFirst(rc?.payments?.[0]?.type)}
            </div>
          ),
          isDefault: true,
          width: 120,
        },
        {
          name: 'paymentStatus',
          title: i18n.t('orders.paymentStatus'),
          render: (dt: string) => (
            <p style={{ minWidth: 120, color: PAYMENT_STATUS[dt]?.color }}>
              {i18n.t(PAYMENT_STATUS[dt]?.text)}
            </p>
          ),
          isDefault: true,
          width: 120,
        },
        {
          name: 'channelDisplay',
          title: i18n.t('orders.channel'),
          render: (d: string) => <p>{upperFirst(d)}</p>,
        },
        {
          name: 'locationId',
          title: i18n.t('orders.warehouse'),
        },
        {
          name: 'note',
          title: i18n.t('orders.note'),
          render: (d: string) => (
            <p className="min-w-[200px]">{upperFirst(d)}</p>
          ),
        },
        {
          name: 'orderType',
          title: i18n.t('orders.type'),
          render: (d: DynamicObjectType, rc: DynamicObjectType) => (
            <p>{i18n.t(ORDER_TYPE_CONST[rc?.orderType]?.text || '')}</p>
          ),
        },
        {
          name: 'isTo',
          title: i18n.t('orders.isTo'),
          render: (d: boolean) =>
            d ? <span className="text-[#EC5500]">TO</span> : <></>,
          isDefault: true,
        },
      ];
    case 'FAIL_QC':
      return [
        {
          name: 'orderNumber',
          title: 'Order ID',
          render: (d: string, record: DynamicObjectType) => (
            <Link to={`/order-management/${record?.id}/show`}>
              <Typography.Paragraph
                ellipsis={{ rows: 1 }}
                style={{ width: 200, color: '#AB2328' }}
              >
                {d}
              </Typography.Paragraph>
            </Link>
          ),
          isDefault: true,
        },
        {
          name: 'createdAt',
          title: 'Created Time',
          render: (d) => <p style={{ minWidth: 150 }}>{formatDateTime(d)}</p>,
          width: 150,
          isDefault: true,
          sorter: true,
        },
        {
          name: 'statusOms',
          title: i18n.t('orders.status'),
          render: (d, record) => (
            <div style={{ minWidth: 150 }}>
              <OrderStatusLabel record={record} />
            </div>
          ),
          isDefault: true,
        },
        {
          name: 'saleOrderId',
          title: i18n.t('orders.saleOrderId'),
          isDefault: true,
          render: (d: string) => <div className="min-w-[120px]">{d}</div>,
        },
        {
          name: 'partner',
          title: i18n.t('orders.deliveryPartner'),
          render: (dt: DynamicObjectType, rc: DynamicObjectType) => {
            return (
              <div className="min-w-[120px]">
                {rc?.shippingCarrier?.name || ''}
              </div>
            );
          },
          isDefault: true,
        },
        {
          name: 'deliveryCode',
          title: i18n.t('orders.shippingNumber'),
          render: (dt, rc) => {
            return <div className="min-w-[150px]">{rc?.deliveryCode}</div>;
          },
          isDefault: true,
        },
        {
          name: 'deliveryPickupTime',
          title: i18n.t('orders.deliveryTime'),
          render: (d) => <p style={{ minWidth: 150 }}>{formatDateTime(d)}</p>,
          isDefault: true,
        },
        {
          name: 'returnedTime',
          title: i18n.t('orders.returnedTime'),
          render: (d) => (
            <div style={{ minWidth: 150 }}>{d ? formatDateTime(d) : ''}</div>
          ),
          isDefault: true,
        },
        {
          name: 'customer',
          title: i18n.t('orders.customer'),
          render: (d: DynamicObjectType, rc: DynamicObjectType) => {
            const data =
              rc?.isMarketPlace || !rc?.customerId ? rc?.address : rc?.customer;
            return (
              <div className="color-dark-gray fw-400">{`${data?.firstName || ''} ${data?.lastName || ''}`}</div>
            );
          },
          isDefault: true,
          width: 120,
        },
        {
          name: 'itemsCount',
          title: i18n.t('orders.numberOfItems'),
          render: (d: number) => <p className="min-w-[100px]">{d || 0}</p>,
          isDefault: true,
          sorter: true,
        },
        {
          name: 'total',
          title: i18n.t('orders.total'),
          render: (d) => <div style={{ minWidth: 120 }}>{formatMoney(d)}</div>,
          width: 120,
          sorter: true,
        },
        {
          name: 'paymentMethod',
          title: i18n.t('orders.paymentMethod'),
          render: (d, rc: DynamicObjectType) => (
            <div style={{ minWidth: 120 }}>
              {upperFirst(rc?.payments?.[0]?.type)}
            </div>
          ),
          isDefault: true,
          width: 120,
        },
        {
          name: 'paymentStatus',
          title: i18n.t('orders.paymentStatus'),
          render: (dt: string) => (
            <p style={{ minWidth: 120, color: PAYMENT_STATUS[dt]?.color }}>
              {i18n.t(PAYMENT_STATUS[dt]?.text)}
            </p>
          ),
          isDefault: true,
          width: 120,
        },
        {
          name: 'handedOverTime',
          title: i18n.t('orders.handedOverTime'),
          render: (d) => (
            <div style={{ minWidth: 120 }}>{d ? formatDateTime(d) : ''}</div>
          ),
        },
        {
          name: 'channelDisplay',
          title: i18n.t('orders.channel'),
          render: (d: string) => <p>{upperFirst(d)}</p>,
        },
        {
          name: 'locationId',
          title: i18n.t('orders.warehouse'),
        },
        {
          name: 'note',
          title: i18n.t('orders.note'),
          render: (d: string) => (
            <p className="min-w-[200px]">{upperFirst(d)}</p>
          ),
        },
        {
          name: 'orderType',
          title: i18n.t('orders.type'),
          render: (d: DynamicObjectType, rc: DynamicObjectType) => (
            <p>{i18n.t(ORDER_TYPE_CONST[rc?.orderType]?.text || '')}</p>
          ),
        },
        {
          name: 'isTo',
          title: i18n.t('orders.isTo'),
          render: (d: boolean) =>
            d ? <span className="text-[#EC5500]">TO</span> : <></>,
          isDefault: true,
        },
      ];
    case 'MANUALLY_PENDING':
      return [
        {
          name: 'orderNumber',
          title: 'Order ID',
          render: (d: string, record: DynamicObjectType) => (
            <Link to={`/order-management/${record?.id}/show`}>
              <Typography.Paragraph
                ellipsis={{ rows: 1 }}
                style={{ width: 200, color: '#AB2328' }}
              >
                #{d}
              </Typography.Paragraph>
            </Link>
          ),
          isDefault: true,
        },
        {
          name: 'createdAt',
          title: 'Created Time',
          render: (d) => (
            <p style={{ minWidth: 150 }}>{d ? formatDateTime(d) : ''}</p>
          ),
          width: 150,
          isDefault: true,
          sorter: true,
        },
        {
          name: 'statusOms',
          title: i18n.t('orders.status'),
          render: (d, record) => <OrderStatusLabel record={record} />,
          isDefault: true,
        },
        {
          name: 'reasonPending',
          title: i18n.t('orders.pendingReason'),
          isDefault: true,
        },
        {
          name: 'customer',
          title: i18n.t('orders.customer'),
          render: (d: DynamicObjectType, rc: DynamicObjectType) => {
            const data =
              rc?.isMarketPlace || !rc?.customerId ? rc?.address : rc?.customer;
            return (
              <div className="color-dark-gray fw-400">{`${data?.firstName || ''} ${data?.lastName || ''}`}</div>
            );
          },
          isDefault: true,
          width: 120,
        },
        {
          name: 'itemsCount',
          title: i18n.t('orders.numberOfItems'),
          render: (d: number) => <p className="min-w-[100px]">{d || 0}</p>,
          isDefault: true,
          sorter: true,
        },
        {
          name: 'total',
          title: i18n.t('orders.total'),
          render: (d) => <div style={{ minWidth: 120 }}>{formatMoney(d)}</div>,
          width: 120,
          sorter: true,
        },
        {
          name: 'paymentMethod',
          title: i18n.t('orders.paymentMethod'),
          render: (d, rc: DynamicObjectType) => (
            <div style={{ minWidth: 120 }}>
              {upperFirst(rc?.payments?.[0]?.type)}
            </div>
          ),
          isDefault: true,
          width: 120,
        },
        {
          name: 'paymentStatus',
          title: i18n.t('orders.paymentStatus'),
          render: (dt: string) => (
            <p style={{ minWidth: 120, color: PAYMENT_STATUS[dt]?.color }}>
              {i18n.t(PAYMENT_STATUS[dt]?.text)}
            </p>
          ),
          isDefault: true,
          width: 120,
        },
        {
          name: 'channelDisplay',
          title: i18n.t('orders.channel'),
          render: (d: string) => <p>{upperFirst(d)}</p>,
        },
        {
          name: 'locationId',
          title: i18n.t('orders.warehouse'),
        },
        {
          name: 'note',
          title: i18n.t('orders.note'),
          render: (d: string) => (
            <p className="min-w-[200px]">{upperFirst(d)}</p>
          ),
        },
        {
          name: 'orderType',
          title: i18n.t('orders.type'),
          render: (d: DynamicObjectType, rc: DynamicObjectType) => (
            <p>{i18n.t(ORDER_TYPE_CONST[rc?.orderType]?.text || '')}</p>
          ),
        },
        {
          name: 'isTo',
          title: i18n.t('orders.isTo'),
          render: (d: boolean) =>
            d ? <span className="text-[#EC5500]">TO</span> : <></>,
          isDefault: true,
        },
      ];
    case 'AUTOMATICALLY_PENDING':
      return [
        {
          name: 'orderNumber',
          title: 'Order ID',
          render: (d: string, record: DynamicObjectType) => (
            <Link to={`/order-management/${record?.id}/show`}>
              <Typography.Paragraph
                ellipsis={{ rows: 1 }}
                style={{ width: 200, color: '#AB2328' }}
              >
                #{d}
              </Typography.Paragraph>
            </Link>
          ),
          isDefault: true,
        },
        {
          name: 'createdAt',
          title: 'Created Time',
          render: (d) => (
            <p style={{ minWidth: 150 }}>{d ? formatDateTime(d) : ''}</p>
          ),
          width: 150,
          isDefault: true,
          sorter: true,
        },
        {
          name: 'statusOms',
          title: i18n.t('orders.status'),
          render: (d, record) => <OrderStatusLabel record={record} />,
          isDefault: true,
        },
        {
          name: 'reasonPending',
          title: i18n.t('orders.pendingReason'),
          isDefault: true,
        },
        {
          name: 'customer',
          title: i18n.t('orders.customer'),
          render: (d: DynamicObjectType, rc: DynamicObjectType) => {
            const data =
              rc?.isMarketPlace || !rc?.customerId ? rc?.address : rc?.customer;
            return (
              <div className="color-dark-gray fw-400">{`${data?.firstName || ''} ${data?.lastName || ''}`}</div>
            );
          },
          isDefault: true,
          width: 120,
        },
        {
          name: 'itemsCount',
          title: i18n.t('orders.numberOfItems'),
          render: (d: number) => <p className="min-w-[100px]">{d || 0}</p>,
          isDefault: true,
          sorter: true,
        },
        {
          name: 'total',
          title: i18n.t('orders.total'),
          render: (d) => <div style={{ minWidth: 120 }}>{formatMoney(d)}</div>,
          width: 120,
          sorter: true,
        },
        {
          name: 'paymentMethod',
          title: i18n.t('orders.paymentMethod'),
          render: (d, rc: DynamicObjectType) => (
            <div style={{ minWidth: 120 }}>
              {upperFirst(rc?.payments?.[0]?.type)}
            </div>
          ),
          isDefault: true,
          width: 120,
        },
        {
          name: 'paymentStatus',
          title: i18n.t('orders.paymentStatus'),
          render: (dt: string) => (
            <p style={{ minWidth: 120, color: PAYMENT_STATUS[dt]?.color }}>
              {i18n.t(PAYMENT_STATUS[dt]?.text)}
            </p>
          ),
          isDefault: true,
          width: 120,
        },
        {
          name: 'channelDisplay',
          title: i18n.t('orders.channel'),
          render: (d: string) => <p>{upperFirst(d)}</p>,
        },
        {
          name: 'locationId',
          title: i18n.t('orders.warehouse'),
        },
        {
          name: 'note',
          title: i18n.t('orders.note'),
          render: (d: string) => (
            <p className="min-w-[200px]">{upperFirst(d)}</p>
          ),
        },
        {
          name: 'orderType',
          title: i18n.t('orders.type'),
          render: (d: DynamicObjectType, rc: DynamicObjectType) => (
            <p>{i18n.t(ORDER_TYPE_CONST[rc?.orderType]?.text || '')}</p>
          ),
        },
        {
          name: 'isTo',
          title: i18n.t('orders.isTo'),
          render: (d: boolean) =>
            d ? <span className="text-[#EC5500]">TO</span> : <></>,
          isDefault: true,
        },
      ];
    case 'EXCHANGE_PENDING':
      return [
        {
          name: 'orderNumber',
          title: 'Order ID',
          render: (d: string, record: DynamicObjectType) => (
            <Link to={`/order-management/${record?.id}/show`}>
              <Typography.Paragraph
                ellipsis={{ rows: 1 }}
                style={{ width: 200, color: '#AB2328' }}
              >
                #{d}
              </Typography.Paragraph>
            </Link>
          ),
          isDefault: true,
        },
        {
          name: 'createdAt',
          title: 'Created Time',
          render: (d) => (
            <p style={{ minWidth: 150 }}>{d ? formatDateTime(d) : ''}</p>
          ),
          width: 150,
          isDefault: true,
          sorter: true,
        },
        {
          name: 'statusOms',
          title: i18n.t('orders.status'),
          render: (d, record) => <OrderStatusLabel record={record} />,
          isDefault: true,
        },
        {
          name: 'reasonPending',
          title: i18n.t('orders.pendingReason'),
          isDefault: true,
        },
        {
          name: 'customer',
          title: i18n.t('orders.customer'),
          render: (d: DynamicObjectType, rc: DynamicObjectType) => {
            const data =
              rc?.isMarketPlace || !rc?.customerId ? rc?.address : rc?.customer;
            return (
              <div className="color-dark-gray fw-400">{`${data?.firstName || ''} ${data?.lastName || ''}`}</div>
            );
          },
          isDefault: true,
          width: 120,
        },
        {
          name: 'itemsCount',
          title: i18n.t('orders.numberOfItems'),
          render: (d: number) => <p className="min-w-[100px]">{d || 0}</p>,
          isDefault: true,
          sorter: true,
        },
        {
          name: 'total',
          title: i18n.t('orders.total'),
          render: (d) => <div style={{ minWidth: 120 }}>{formatMoney(d)}</div>,
          width: 120,
          sorter: true,
        },
        {
          name: 'paymentMethod',
          title: i18n.t('orders.paymentMethod'),
          render: (d, rc: DynamicObjectType) => (
            <div style={{ minWidth: 120 }}>
              {upperFirst(rc?.payments?.[0]?.type)}
            </div>
          ),
          isDefault: true,
          width: 120,
        },
        {
          name: 'paymentStatus',
          title: i18n.t('orders.paymentStatus'),
          render: (dt: string) => (
            <p style={{ minWidth: 120, color: PAYMENT_STATUS[dt]?.color }}>
              {i18n.t(PAYMENT_STATUS[dt]?.text)}
            </p>
          ),
          isDefault: true,
          width: 120,
        },
        {
          name: 'channelDisplay',
          title: i18n.t('orders.channel'),
          render: (d: string) => <p>{upperFirst(d)}</p>,
        },
        {
          name: 'locationId',
          title: i18n.t('orders.warehouse'),
        },
        {
          name: 'note',
          title: i18n.t('orders.note'),
          render: (d: string) => (
            <p className="min-w-[200px]">{upperFirst(d)}</p>
          ),
        },
        {
          name: 'orderType',
          title: i18n.t('orders.type'),
          render: (d: DynamicObjectType, rc: DynamicObjectType) => (
            <p>{i18n.t(ORDER_TYPE_CONST[rc?.orderType]?.text || '')}</p>
          ),
        },
        {
          name: 'isTo',
          title: i18n.t('orders.isTo'),
          render: (d: boolean) =>
            d ? <span className="text-[#EC5500]">TO</span> : <></>,
          isDefault: true,
        },
      ];
    // case 'PENDING_FOR_TAB_TRY_BUY': {
    //   return [];
    // }
    default:
      return [
        {
          name: 'orderNumber',
          title: 'Order ID',
          render: (d: string, record: DynamicObjectType) => (
            <Link to={`/order-management/${record?.id}/show`}>
              <Typography.Paragraph
                ellipsis={{ rows: 1 }}
                style={{ width: 200, color: '#AB2328' }}
              >
                {d}
              </Typography.Paragraph>
            </Link>
          ),
          isDefault: true,
        },
        {
          name: 'createdAt',
          title: 'Created Time',
          render: (d) => (
            <p style={{ minWidth: 150 }}>{d ? formatDateTime(d) : ''}</p>
          ),
          width: 150,
          isDefault: true,
          sorter: true,
        },
        {
          name: 'statusOms',
          title: i18n.t('orders.status'),
          render: (d, record) => <OrderStatusLabel record={record} />,
          isDefault: true,
        },
        {
          name: 'customer',
          title: i18n.t('orders.customer'),
          render: (d: DynamicObjectType, rc: DynamicObjectType) => {
            const data =
              rc?.isMarketPlace || !rc?.customerId ? rc?.address : rc?.customer;
            return (
              <div className="color-dark-gray fw-400">{`${data?.firstName || ''} ${data?.lastName || ''}`}</div>
            );
          },
          isDefault: true,
          width: 120,
        },
        {
          name: 'itemsCount',
          title: i18n.t('orders.numberOfItems'),
          render: (d: number) => <p className="min-w-[100px]">{d || 0}</p>,
          isDefault: true,
          sorter: true,
        },
        {
          name: 'total',
          title: i18n.t('orders.total'),
          render: (d) => <div style={{ minWidth: 120 }}>{formatMoney(d)}</div>,
          isDefault: true,
          width: 120,
          sorter: true,
        },
        {
          name: 'paymentMethod',
          title: i18n.t('orders.paymentMethod'),
          render: (d, rc: DynamicObjectType) => (
            <div style={{ minWidth: 120 }}>
              {upperFirst(rc?.payments?.[0]?.type)}
            </div>
          ),
          isDefault: true,
          width: 120,
        },
        {
          name: 'paymentStatus',
          title: i18n.t('orders.paymentStatus'),
          render: (dt: string) => (
            <p style={{ minWidth: 120, color: PAYMENT_STATUS[dt]?.color }}>
              {i18n.t(PAYMENT_STATUS[dt]?.text)}
            </p>
          ),
          isDefault: true,
          width: 120,
        },
        {
          name: 'note',
          title: i18n.t('orders.note'),
          render: (d: string) => (
            <p className="min-w-[200px]">{upperFirst(d)}</p>
          ),
        },
        {
          name: 'orderType',
          title: i18n.t('orders.type'),
          render: (d: DynamicObjectType, rc: DynamicObjectType) => (
            <p>{i18n.t(ORDER_TYPE_CONST[rc?.orderType]?.text || '')}</p>
          ),
        },
        {
          name: 'locationId',
          title: i18n.t('orders.warehouse'),
        },
        {
          name: 'channelDisplay',
          title: i18n.t('orders.channel'),
          render: (d: string) => <p>{upperFirst(d)}</p>,
        },
        {
          name: 'partner',
          title: i18n.t('orders.deliveryPartner'),
          render: (dt: DynamicObjectType, rc: DynamicObjectType) => {
            return (
              <div className="min-w-[120px]">
                {rc?.shippingCarrier?.name || ''}
              </div>
            );
          },
        },
        {
          name: 'deliveryCode',
          title: i18n.t('orders.shippingNo'),
          render: (dt, rc) => {
            return <div className="min-w-[150px]">{rc?.deliveryCode}</div>;
          },
        },
        {
          name: 'confirmationTime',
          title: i18n.t('orders.confirmationTime'),
          render: (d) => (
            <div style={{ minWidth: 120 }}>{d ? formatDateTime(d) : ''}</div>
          ),
        },
        {
          name: 'saleOrderId',
          title: i18n.t('orders.saleOrderId'),
          render: (d: string) => <p style={{ minWidth: 150 }}>{d || ''}</p>,
        },
        {
          name: 'soCreatedTime',
          title: i18n.t('orders.soCreatedTime'),
          render: (d) => (
            <div style={{ minWidth: 120 }}>{d ? formatDateTime(d) : ''}</div>
          ),
        },
        {
          name: 'wspId',
          title: i18n.t('orders.wspId'),
          render: (d: string) => <p style={{ minWidth: 100 }}>{d || ''}</p>,
        },
        {
          name: 'wspCreatedTime',
          title: i18n.t('orders.wspCreatedTime'),
          render: (d) => (
            <div style={{ minWidth: 120 }}>{d ? formatDateTime(d) : ''}</div>
          ),
        },
        {
          name: 'handedOverTime',
          title: i18n.t('orders.handedOverTime'),
          render: (d) => (
            <div style={{ minWidth: 120 }}>{d ? formatDateTime(d) : ''}</div>
          ),
        },
        {
          name: 'postInvoiceDate',
          title: i18n.t('orders.postInvoiceDate'),
          render: (d) => (
            <div style={{ minWidth: 120 }}>{d ? formatDateTime(d) : ''}</div>
          ),
        },
        {
          name: 'invoiceNumber',
          title: i18n.t('orders.invoiceNumber'),
        },
        {
          name: 'rejectedTime',
          title: i18n.t('orders.rejectedTime'),
          render: (d) => (
            <div style={{ minWidth: 120 }}>{d ? formatDateTime(d) : ''}</div>
          ),
        },
        {
          name: 'returnedTime',
          title: i18n.t('orders.returnedTime'),
          render: (d) => (
            <div style={{ minWidth: 120 }}>{d ? formatDateTime(d) : ''}</div>
          ),
        },
        {
          name: 'lostTime',
          title: i18n.t('orders.lostTime'),
          render: (d) => (
            <div style={{ minWidth: 120 }}>{d ? formatDateTime(d) : ''}</div>
          ),
        },
        {
          name: 'lostReason',
          title: i18n.t('orders.lostReason'),
        },
        {
          name: 'deliveryFailedTime',
          title: i18n.t('orders.deliveryFailedTime'),
          render: (d) => (
            <div style={{ minWidth: 120 }}>{d ? formatDateTime(d) : ''}</div>
          ),
        },
        {
          name: 'cancelTime',
          title: i18n.t('orders.cancelTime'),
          render: (d) => (
            <div style={{ minWidth: 120 }}>{d ? formatDateTime(d) : ''}</div>
          ),
        },
        {
          name: 'cancelReason',
          title: i18n.t('orders.cancelReason'),
        },
        {
          name: 'canceledBy',
          title: i18n.t('orders.cancelledBy'),
          render: (dt: DynamicObjectType) => (
            <div className="color-dark-gray fw-400">{`${dt?.firstName || ''} ${dt?.lastName || ''}`}</div>
          ),
        },
        {
          name: 'pendingReason',
          title: i18n.t('orders.pendingReason'),
          render: (d: string) => <p style={{ minWidth: 150 }}>{d || ''}</p>,
        },
        {
          name: 'ppNumber',
          title: i18n.t('orders.pp'),
        },
        {
          name: 'isTo',
          title: i18n.t('orders.isTo'),
          render: (d: boolean) =>
            d ? <span className="text-[#EC5500]">TO</span> : <></>,
          isDefault: true,
        },
        {
          name: 'newOrderExchangeId',
          title: i18n.t('orders.isExchange'),
          render: (d) => (
            <Tag
              color={d ? 'blue' : 'red'}
              style={{ borderRadius: 5, padding: '5px 10px', border: 'none' }}
            >
              {d ? 'Yes' : 'No'}
            </Tag>
          ),
          isDefault: true,
        },
      ];
  }
};
